import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from "@angular/router";
import { filter, map, Observable, Subscription } from "rxjs";
import { AppConfiguratorComponent } from "./app.configurator";
import { AppSidebarComponent } from "./app.sidebar";
import { AppTopbarComponent } from "./app.topbar";
import { OrganizationDetails } from "../../../../domain/organization";
import { isOrganizationUserRouteData } from "../../../../domain/route-data";
import { NavigationService } from "../../../../services/navigation.service";
import { UserService } from "../../../../services/user.service";
import { TosWarningBannerComponent } from "../../components/tos-warning-banner/tos-warning-banner.component";
import { LayoutService } from "../service/layout.service";

@Component({
  selector: "app-layout",
  standalone: true,
  imports: [
    CommonModule,
    AppTopbarComponent,
    AppSidebarComponent,
    RouterModule,
    AppConfiguratorComponent,
    TosWarningBannerComponent,
  ],
  template: `<div class="layout-container" [ngClass]="containerClass">
    <div app-sidebar></div>
    <div class="layout-content-wrapper">
      <div app-topbar></div>
      <app-tos-warning-banner [organization]="organization$ | async" [isInternalUser]="isInternalUser$ | async">
      </app-tos-warning-banner>
      <div class="layout-content">
        <router-outlet></router-outlet>
      </div>
    </div>
    <app-configurator></app-configurator>
    <div class="layout-mask animate-fadein"></div>
  </div> `,
})
export class AppLayoutComponent implements OnDestroy, OnInit {
  overlayMenuOpenSubscription: Subscription;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menuOutsideClickListener: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menuScrollListener: any;

  organization$!: Observable<OrganizationDetails | null>;
  isInternalUser$!: Observable<boolean>;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) appTopBar!: AppTopbarComponent;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private navigationService: NavigationService,
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen("document", "click", (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });
      }
      if (
        (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(this.appSidebar.menuContainer.nativeElement, "scroll", () => {
          if (this.layoutService.isDesktop()) {
            this.hideMenu();
          }
        });
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.hideMenu();
      });
  }

  ngOnInit() {
    this.organization$ = this.route.data.pipe(
      map((data) => (isOrganizationUserRouteData(data) ? data["organization"] : null)),
    );

    this.isInternalUser$ = this.userService.getSelf$().pipe(
      map((user) => {
        if (!user) {
          return false;
        }
        const isInternal = user.isInternal === true;
        return isInternal;
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isOutsideClicked(event: any) {
    const sidebarEl = document.querySelector(".layout-sidebar");
    const topbarButtonEl = document.querySelector(".topbar-menubutton");

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    this.layoutService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add("blocked-scroll");
    } else {
      document.body.className += " blocked-scroll";
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove("blocked-scroll");
    } else {
      document.body.className = document.body.className.replace(
        new RegExp("(^|\\b)" + "blocked-scroll".split(" ").join("|") + "(\\b|$)", "gi"),
        " ",
      );
    }
  }

  get containerClass() {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      "layout-light": !layoutConfig.darkTheme,
      "layout-dark": layoutConfig.darkTheme,
      "layout-colorscheme-menu": layoutConfig.menuTheme === "colorScheme",
      "layout-primarycolor-menu": layoutConfig.menuTheme === "primaryColor",
      "layout-transparent-menu": layoutConfig.menuTheme === "transparent",
      "layout-overlay": layoutConfig.menuMode === "overlay",
      "layout-static": layoutConfig.menuMode === "static",
      "layout-slim": layoutConfig.menuMode === "slim",
      "layout-slim-plus": layoutConfig.menuMode === "slim-plus",
      "layout-horizontal": layoutConfig.menuMode === "horizontal",
      "layout-reveal": layoutConfig.menuMode === "reveal",
      "layout-drawer": layoutConfig.menuMode === "drawer",
      "layout-static-inactive": layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === "static",
      "layout-overlay-active": layoutState.overlayMenuActive,
      "layout-mobile-active": layoutState.staticMenuMobileActive,
      "layout-sidebar-active": layoutState.sidebarActive,
      "layout-sidebar-anchored": layoutState.anchored,
    };
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
